import { Component, signal, computed, inject, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DragDropModule, CdkDragDrop } from '@angular/cdk/drag-drop';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import * as XLSX from 'xlsx';

import { PssLoadingComponent } from '../../../tools/template/pss/pss-loading/pss-loading.component';
import { ApiDashboardBuilderService } from '../../../core/services/api/api-dashboard-builder.service';
import { MapperDashboardBuilderService } from '../../../core/mappers/mapper-dashboard-builder.service';
import { SiteStateService } from '../../../core/services/site-state.service';
import { DashboardNode, NodeConnection } from '../../../core/models/dashboard-builder.model';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    MatDatepickerModule,
    MatNativeDateModule,
    PssLoadingComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit {
  // Service Injection
  private apiService = inject(ApiDashboardBuilderService);
  private mapperService = inject(MapperDashboardBuilderService);
  private siteState = inject(SiteStateService);

  // Reactive State signals
  isLoading = signal<boolean>(false);
  nodes = signal<DashboardNode[]>([]);
  connections = signal<NodeConnection[]>([]);
  selectedConnection = signal<NodeConnection | null>(null);
  comparisonData = signal<any | null>(null);
  renderTrigger = signal<number>(0);

  // Custom Autocomplete Site Selector States
  searchSiteText = signal<string>('');
  showSiteDropdown = signal<boolean>(false);
  selectedSite = signal<any>({ id: 1, name: 'ลาดพร้าว 101' });

  // Connection Line Dragging state
  drawingConnection = signal<{
    fromNodeId: string;
    fromAnchor: 'left' | 'right';
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null);

  // Sidebar widget categories structure (matching layout accordion requirements)
  categories = [
    {
      name: 'ระบบที่จอดรถ (Parking)',
      icon: 'local_parking',
      items: [
        { type: 'total-cars', title: 'Total Registered Cars', icon: 'directions_car', desc: 'KPI: Active & registered vehicles summary' },
        { type: 'occupancy', title: 'Spot Occupancy Rate', icon: 'donut_large', desc: 'Gauge: Real-time parking slot utility' }
      ]
    },
    {
      name: 'ระบบการเงิน (Finance)',
      icon: 'account_balance_wallet',
      items: [
        { type: 'revenue', title: 'Revenue Overview', icon: 'payments', desc: 'KPI: Sum of EV charging and parking fee revenues' },
        { type: 'monthly-stats', title: 'Monthly Trend Stats', icon: 'leaderboard', desc: 'Chart: Monthly comparison of parking & EV' }
      ]
    },
    {
      name: 'ประวัติและบันทึก (Activity Logs)',
      icon: 'description',
      items: [
        { type: 'transactions', title: 'Recent Parking Logs', icon: 'history', desc: 'List: Live logs of vehicle park transactions' }
      ]
    }
  ];

  // Accordion active expansions signals
  expandedCategories = signal<Record<string, boolean>>({
    'ระบบที่จอดรถ (Parking)': true,
    'ระบบการเงิน (Finance)': true,
    'ประวัติและบันทึก (Activity Logs)': true
  });

  // Computes list of available sites combining user profile and sandbox defaults
  sitesList = computed(() => {
    const stateSites = this.siteState.sites();
    if (stateSites && stateSites.length > 0) {
      return stateSites;
    }
    return [
      { id: 1, name: 'ลาดพร้าว 101' },
      { id: 2, name: 'สยามสแควร์' },
      { id: 3, name: 'อโศก ทาวเวอร์' },
      { id: 4, name: 'รัชดาพลาซ่า' }
    ];
  });

  // Autocomplete live-filtered sites
  filteredSites = computed(() => {
    const query = this.searchSiteText().toLowerCase().trim();
    const list = this.sitesList();
    if (!query) return list;
    return list.filter(site => site.name.toLowerCase().includes(query));
  });

  // Computes active connection lines in canvas real-time
  renderedLines = computed(() => {
    const list = this.connections();
    const nodeData = this.nodes();
    const trigger = this.renderTrigger();

    return list.map(conn => {
      const start = this.getAnchorCoordinates(conn.fromNodeId, conn.fromAnchor);
      const end = this.getAnchorCoordinates(conn.toNodeId, conn.toAnchor);
      
      const dx = Math.abs(end.x - start.x);
      const cp = Math.max(dx / 2, 40);
      const path = `M ${start.x} ${start.y} C ${start.x + (conn.fromAnchor === 'right' ? cp : -cp)} ${start.y}, ${end.x + (conn.toAnchor === 'right' ? cp : -cp)} ${end.y}, ${end.x} ${end.y}`;

      const midX = (start.x + end.x) / 2;
      const midY = (start.y + end.y) / 2;

      const fromNode = nodeData.find(n => n.id === conn.fromNodeId);
      const toNode = nodeData.find(n => n.id === conn.toNodeId);
      let differenceLabel = '';

      if (fromNode?.data && toNode?.data && fromNode.type === toNode.type) {
        if (fromNode.type === 'total-cars') {
          const diff = fromNode.data.activeCars - toNode.data.activeCars;
          differenceLabel = `Δ ${diff > 0 ? '+' : ''}${diff}`;
        } else if (fromNode.type === 'revenue') {
          const diff = fromNode.data.totalRevenue - toNode.data.totalRevenue;
          const diffPercent = ((diff / (toNode.data.totalRevenue || 1)) * 100).toFixed(0);
          differenceLabel = `Δ ${Number(diffPercent) > 0 ? '+' : ''}${diffPercent}%`;
        } else if (fromNode.type === 'occupancy') {
          const diff = (fromNode.data.occupancyRate - toNode.data.occupancyRate).toFixed(1);
          differenceLabel = `Δ ${Number(diff) > 0 ? '+' : ''}${diff}%`;
        }
      }

      return {
        id: conn.id,
        path,
        midX,
        midY,
        differenceLabel,
        connection: conn
      };
    });
  });

  // ================= Normal Functions =================

  /**
   * Initializes site selector text on component lifecycle start
   * ดึงข้อมูลสถานที่ตั้งต้นเมื่อเปิดใช้งานโมดูล
   */
  ngOnInit() {
    const activeSite = this.siteState.selectedSite();
    if (activeSite) {
      this.selectedSite.set(activeSite);
      this.searchSiteText.set(activeSite.name);
    } else {
      const fallback = this.sitesList()[0];
      this.selectedSite.set(fallback);
      this.searchSiteText.set(fallback?.name || '');
    }
  }

  /**
   * Selects a target site option from the autocomplete dropdown
   * เลือกสถานที่จอดรถเพื่อกำหนดค่าการดึงรายงาน API
   */
  selectSite(site: any) {
    this.selectedSite.set(site);
    this.searchSiteText.set(site.name);
    this.showSiteDropdown.set(false);
    console.log('[Site Changed] Selected location configured:', site);
  }

  /**
   * Safe closure logic on input blur to auto-revert if query is invalid
   * ตรวจสอบความถูกต้องของชื่อสถานที่ที่พิมพ์ค้างไว้เมื่อกดปุ่มเลื่อนหน้าจอ
   */
  closeSiteDropdown() {
    setTimeout(() => {
      const query = this.searchSiteText().trim().toLowerCase();
      const match = this.sitesList().find(s => s.name.toLowerCase() === query);
      if (match) {
        this.selectedSite.set(match);
        this.searchSiteText.set(match.name);
      } else {
        this.searchSiteText.set(this.selectedSite()?.name || '');
      }
      this.showSiteDropdown.set(false);
    }, 200);
  }

  /**
   * Toggles category sidebar accordion expansion state
   * เปิดหรือปิดแถบตัวกรองการลากกลุ่มเมนูใน Sidebar
   */
  toggleCategory(catName: string) {
    this.expandedCategories.update(state => ({
      ...state,
      [catName]: !state[catName]
    }));
  }

  /**
   * Formats a standard Date object into YYYY-MM-DD string
   * แปลงวัตถุวันที่เพื่อส่งผ่านพารามิเตอร์ Query ของ API
   */
  formatDate(date: Date | undefined): string | undefined {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Triggers when a node's custom date range filter is modified
   * เมื่อผู้ใช้คลิกเปลี่ยนแปลงฟิลเตอร์ช่วงวันที่ของตัวการ์ดใบนั้นๆ
   */
  onNodeDateChanged(node: DashboardNode) {
    if (node.startDate && node.endDate) {
      node.isLoading = true;
      const targetSite = node.siteId || String(this.selectedSite().id);
      this.fetchDataForNode(node, targetSite);
      
      // Update comparison panel dynamically if node is selected
      const activeConn = this.selectedConnection();
      if (activeConn && (activeConn.fromNodeId === node.id || activeConn.toNodeId === node.id)) {
        this.selectConnection(activeConn);
      }
    }
  }

  /**
   * Exports single node card statistics to Excel sheet
   * แปลงข้อมูลรายงานของการ์ดนั้นๆ ออกเป็นไฟล์กระดาษคำนวณ Excel
   */
  exportToExcel(event: MouseEvent, node: DashboardNode) {
    event.stopPropagation();
    const data = node.data;
    if (!data) return;

    let rows: any[] = [];
    if (node.type === 'total-cars') {
      rows = [{
        'Active Cars': data.activeCars,
        'Registered Cars': data.registeredCars,
        'Active Percentage': `${data.percentageActive}%`,
        'Site ID': node.siteId,
        'Date Range': `${this.formatDate(node.startDate)} to ${this.formatDate(node.endDate)}`
      }];
    } else if (node.type === 'revenue') {
      rows = [{
        'Total Revenue (THB)': data.totalRevenue,
        'Parking Revenue (THB)': data.parkingRevenue,
        'EV Revenue (THB)': data.evRevenue,
        'Site ID': node.siteId,
        'Date Range': `${this.formatDate(node.startDate)} to ${this.formatDate(node.endDate)}`
      }];
    } else if (node.type === 'occupancy') {
      rows = [{
        'Total Capacity Spots': data.totalSpots,
        'Occupied Spots': data.occupiedSpots,
        'Occupancy Rate': `${data.occupancyRate}%`,
        'Site ID': node.siteId,
        'Date Range': `${this.formatDate(node.startDate)} to ${this.formatDate(node.endDate)}`
      }];
    } else if (node.type === 'transactions') {
      rows = data.map((t: any) => ({
        'ID': t.id,
        'License Plate': t.licensePlate,
        'Timestamp': t.timeLabel,
        'Amount (THB)': t.amount,
        'Payment Type': t.paymentType,
        'Site ID': node.siteId
      }));
    } else if (node.type === 'monthly-stats') {
      rows = data.labels.map((month: string, idx: number) => ({
        'Month': month,
        'Parking Check-ins': data.parkingCounts[idx] || 0,
        'EV Charging Count': data.evCounts[idx] || 0,
        'Revenue (k THB)': data.revenueData[idx] || 0,
        'Site ID': node.siteId
      }));
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data Report');
    XLSX.writeFile(workbook, `${node.title.replace(/\s+/g, '_')}_Report.xlsx`);
    console.log(`[Excel Download] Saved ${node.title}_Report.xlsx`);
  }

  /**
   * Exports currently active comparative data rows to Excel sheet
   * บันทึกข้อมูลวิเคราะห์เปรียบเทียบระหว่างสองคู่เชื่อมโยงออกเป็นไฟล์ Excel
   */
  exportComparisonToExcel() {
    const data = this.comparisonData();
    if (!data || data.incompatible) return;

    let rows: any[] = [];
    let filename = 'Comparison_Report.xlsx';

    if (data.type === 'total-cars') {
      rows = [
        { Parameter: 'Active Cars', [data.titleA]: data.activeA, [data.titleB]: data.activeB, Difference: data.diffActive },
        { Parameter: 'Active Percentage', [data.titleA]: `${data.percentageA}%`, [data.titleB]: `${data.percentageB}%`, Difference: `${(data.percentageA - data.percentageB).toFixed(1)}%` }
      ];
      filename = 'Total_Cars_Comparison.xlsx';
    } else if (data.type === 'revenue') {
      rows = [
        { Parameter: 'Total Revenue (THB)', [data.titleA]: data.revenueA, [data.titleB]: data.revenueB, Difference: data.diffTotal, 'Diff %': `${data.percentDiff}%` },
        { Parameter: 'Parking Revenue (THB)', [data.titleA]: data.parkingA, [data.titleB]: data.parkingB, Difference: data.parkingA - data.parkingB },
        { Parameter: 'EV Revenue (THB)', [data.titleA]: data.evA, [data.titleB]: data.evB, Difference: data.evA - data.evB }
      ];
      filename = 'Revenue_Comparison.xlsx';
    } else if (data.type === 'occupancy') {
      rows = [
        { Parameter: 'Occupied Spots', [data.titleA]: data.occupiedA, [data.titleB]: data.occupiedB, Difference: data.diffSpots },
        { Parameter: 'Occupancy Rate', [data.titleA]: `${data.rateA}%`, [data.titleB]: `${data.rateB}%`, Difference: `${data.diffRate}%` }
      ];
      filename = 'Occupancy_Comparison.xlsx';
    } else if (data.type === 'monthly-stats') {
      rows = data.items.map((item: any) => ({
        Month: item.month,
        [data.titleA]: `${item.valA}k`,
        [data.titleB]: `${item.valB}k`,
        Difference: `${item.diff}k`
      }));
      filename = 'Monthly_Stats_Comparison.xlsx';
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Comparison Report');
    XLSX.writeFile(workbook, filename);
    console.log(`[Excel Download] Saved Comparison Report: ${filename}`);
  }

  /**
   * Triggers re-rendering when window resizes
   * อัปเดตพิกัดเส้นเชื่อมเมื่อมีการขยาย/ย่อหน้าจอ
   */
  @HostListener('window:resize')
  onResize() {
    this.updateConnectionLines();
  }

  /**
   * Increments frame render trigger signal
   * สั่งให้อัปเดตการคำนวณตำแหน่งเส้นเชื่อม SVG
   */
  updateConnectionLines() {
    this.renderTrigger.set(this.renderTrigger() + 1);
  }

  /**
   * Handles dropping sidebar items on the canvas
   * จัดการการลากไอเทมจากเมนูข้างนอกลงในบอร์ดแคนวาส
   */
  onDrop(event: CdkDragDrop<any>) {
    if (event.previousContainer !== event.container) {
      const canvasEl = document.getElementById('canvas-board');
      if (canvasEl) {
        const rect = canvasEl.getBoundingClientRect();
        const x = event.dropPoint.x - rect.left - 160 + canvasEl.scrollLeft;
        const y = event.dropPoint.y - rect.top - 25 + canvasEl.scrollTop;
        
        const type = event.item.data.type;
        const title = `${event.item.data.title} (${this.selectedSite().name})`;
        this.addNode(type, title, Math.max(10, x), Math.max(10, y));
      }
    }
  }

  /**
   * Adds a new node to the workspace canvas
   * เพิ่ม Node การ์ดอันใหม่ในแคนวาส
   */
  addNode(type: string, title: string, x: number, y: number) {
    const id = `node-${Date.now()}`;
    
    // Set default date range filters (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const newNode: DashboardNode = {
      id,
      type,
      title,
      x,
      y,
      isLoading: true,
      startDate: thirtyDaysAgo,
      endDate: today,
      siteId: String(this.selectedSite().id)
    };

    this.nodes.update(list => [...list, newNode]);
    this.fetchDataForNode(newNode, newNode.siteId || String(this.selectedSite().id));
  }

  /**
   * Removes a node and all of its connection links
   * ลบการ์ดออกพร้อมกับทำลายเส้นเชื่อมที่ผูกไว้ทั้งหมด
   */
  removeNode(nodeId: string) {
    this.connections.update(list => list.filter(c => c.fromNodeId !== nodeId && c.toNodeId !== nodeId));
    this.nodes.update(list => list.filter(n => n.id !== nodeId));
    const sel = this.selectedConnection();
    if (sel && (sel.fromNodeId === nodeId || sel.toNodeId === nodeId)) {
      this.selectedConnection.set(null);
      this.comparisonData.set(null);
    }
    this.updateConnectionLines();
  }

  /**
   * Recalculates paths in real time while node is dragging
   * อัปเดตพิกัดเส้นโค้งขณะจับเลื่อนย้ายการ์ด
   */
  onNodeMoved(event: any, node: DashboardNode) {
    this.updateConnectionLines();
  }

  /**
   * Finalizes position when drag finishes
   * ล็อกตำแหน่งการ์ดเมื่อวางการ์ดเรียบร้อย
   */
  onNodeDragEnded(event: any, node: DashboardNode) {
    const distance = event.distance;
    node.x += distance.x;
    node.y += distance.y;
    event.source.reset();
    this.updateConnectionLines();
  }

  /**
   * Get target anchor center coordinates relative to canvas
   * ดึงพิกัดกึ่งกลางของจุดยึดเส้นเชื่อมต่อเทียบกับตัว Canvas
   */
  getAnchorCoordinates(nodeId: string, anchorSide: 'left' | 'right') {
    const anchorEl = document.getElementById(`anchor-${nodeId}-${anchorSide}`);
    const canvasEl = document.getElementById('canvas-board');
    if (anchorEl && canvasEl) {
      const anchorRect = anchorEl.getBoundingClientRect();
      const canvasRect = canvasEl.getBoundingClientRect();
      return {
        x: anchorRect.left - canvasRect.left + anchorRect.width / 2 + canvasEl.scrollLeft,
        y: anchorRect.top - canvasRect.top + anchorRect.height / 2 + canvasEl.scrollTop
      };
    }
    return { x: 0, y: 0 };
  }

  /**
   * Handles starting connection line drag from an anchor point
   * เริ่มลากเส้นเชือมโยงจากจุดเชื่อม
   */
  startConnectionDrawing(event: MouseEvent, node: DashboardNode, anchor: 'left' | 'right') {
    event.stopPropagation();
    event.preventDefault();
    const start = this.getAnchorCoordinates(node.id, anchor);
    
    this.drawingConnection.set({
      fromNodeId: node.id,
      fromAnchor: anchor,
      x1: start.x,
      y1: start.y,
      x2: start.x,
      y2: start.y
    });

    const onMouseMove = (moveEvent: MouseEvent) => {
      const canvasEl = document.getElementById('canvas-board');
      if (canvasEl) {
        const canvasRect = canvasEl.getBoundingClientRect();
        const currentDrawing = this.drawingConnection();
        if (currentDrawing) {
          this.drawingConnection.set({
            ...currentDrawing,
            x2: moveEvent.clientX - canvasRect.left + canvasEl.scrollLeft,
            y2: moveEvent.clientY - canvasRect.top + canvasEl.scrollTop
          });
        }
      }
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setTimeout(() => {
        this.drawingConnection.set(null);
      }, 50);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  /**
   * Establishes link connections between node anchors
   * เชื่อมโยงคู่การ์ดหลังจากเมาส์ถูกปล่อยบนจุดเชื่อมปลายทาง
   */
  establishConnection(event: MouseEvent, targetNode: DashboardNode, targetAnchor: 'left' | 'right') {
    event.stopPropagation();
    const currentDrawing = this.drawingConnection();
    if (currentDrawing && currentDrawing.fromNodeId !== targetNode.id) {
      const exists = this.connections().some(c => 
        (c.fromNodeId === currentDrawing.fromNodeId && c.toNodeId === targetNode.id && c.fromAnchor === currentDrawing.fromAnchor && c.toAnchor === targetAnchor) ||
        (c.fromNodeId === targetNode.id && c.toNodeId === currentDrawing.fromNodeId && c.fromAnchor === targetAnchor && c.toAnchor === currentDrawing.fromAnchor)
      );

      if (!exists) {
        const newConn: NodeConnection = {
          id: `conn-${Date.now()}`,
          fromNodeId: currentDrawing.fromNodeId,
          toNodeId: targetNode.id,
          fromAnchor: currentDrawing.fromAnchor,
          toAnchor: targetAnchor
        };
        
        this.connections.update(list => [...list, newConn]);
        console.log(`[Connection Created] Linked: ${currentDrawing.fromNodeId} -> ${targetNode.id}`);
        
        this.updateConnectionLines();
        this.selectConnection(newConn);
      }
    }
  }

  /**
   * Focuses and selects a connection path for comparison view
   * เลือกเส้นเชื่อมดังกล่าวเพื่อเรียกดูการเปรียบเทียบข้อมูลแบบคู่
   */
  selectConnection(conn: NodeConnection) {
    this.selectedConnection.set(conn);
    const nodeA = this.nodes().find(n => n.id === conn.fromNodeId);
    const nodeB = this.nodes().find(n => n.id === conn.toNodeId);

    if (nodeA?.data && nodeB?.data) {
      if (nodeA.type === nodeB.type) {
        this.compareNodesData(nodeA, nodeB);
      } else {
        this.comparisonData.set({
          incompatible: true,
          msg: 'ไม่สามารถเปรียบเทียบข้อมูลต่างชนิดกันได้ โปรดเชื่อมโยงหัวข้อประเภทเดียวกัน'
        });
      }
    } else {
      this.comparisonData.set({
        incompatible: true,
        msg: 'กำลังโหลดข้อมูล... โปรดเปรียบเทียบเมื่อตัวการ์ดดึงข้อมูลเสร็จสิ้น'
      });
    }
  }

  /**
   * Performs comparison arithmetic on selected nodes
   * คำนวณความแตกต่างเปรียบเทียบระหว่างกลุ่มข้อมูลที่ถูกจับคู่
   */
  compareNodesData(nodeA: DashboardNode, nodeB: DashboardNode) {
    const dataA = nodeA.data;
    const dataB = nodeB.data;
    
    if (nodeA.type === 'total-cars') {
      const diffActive = dataA.activeCars - dataB.activeCars;
      const diffReg = dataA.registeredCars - dataB.registeredCars;
      this.comparisonData.set({
        type: 'total-cars',
        titleA: nodeA.title,
        titleB: nodeB.title,
        activeA: dataA.activeCars,
        activeB: dataB.activeCars,
        diffActive,
        diffReg,
        percentageA: dataA.percentageActive,
        percentageB: dataB.percentageActive
      });
    } else if (nodeA.type === 'revenue') {
      const diffTotal = dataA.totalRevenue - dataB.totalRevenue;
      const percentDiff = ((diffTotal / (dataB.totalRevenue || 1)) * 100).toFixed(1);
      this.comparisonData.set({
        type: 'revenue',
        titleA: nodeA.title,
        titleB: nodeB.title,
        revenueA: dataA.totalRevenue,
        revenueB: dataB.totalRevenue,
        diffTotal,
        percentDiff,
        evA: dataA.evRevenue,
        evB: dataB.evRevenue,
        parkingA: dataA.parkingRevenue,
        parkingB: dataB.parkingRevenue
      });
    } else if (nodeA.type === 'occupancy') {
      const diffSpots = dataA.occupiedSpots - dataB.occupiedSpots;
      const diffRateNum = dataA.occupancyRate - dataB.occupancyRate;
      const diffRate = diffRateNum.toFixed(1);
      this.comparisonData.set({
        type: 'occupancy',
        titleA: nodeA.title,
        titleB: nodeB.title,
        occupiedA: dataA.occupiedSpots,
        occupiedB: dataB.occupiedSpots,
        rateA: dataA.occupancyRate,
        rateB: dataB.occupancyRate,
        diffSpots,
        diffRate,
        diffRatePositive: diffRateNum > 0
      });
    } else if (nodeA.type === 'monthly-stats') {
      const items = dataA.labels.map((m: string, i: number) => {
        const valA = dataA.revenueData[i] || 0;
        const valB = dataB.revenueData[i] || 0;
        return {
          month: m,
          valA,
          valB,
          diff: valA - valB
        };
      });
      this.comparisonData.set({
        type: 'monthly-stats',
        titleA: nodeA.title,
        titleB: nodeB.title,
        items
      });
    } else {
      this.comparisonData.set({
        incompatible: true,
        msg: 'หัวข้อนี้ไม่มีโหมดเปรียบเทียบเชิงสถิติขั้นสูง'
      });
    }
  }

  /**
   * Resets comparison state overlays
   * ปิดหน้าต่างเปรียบเทียบข้อมูลลง
   */
  clearComparison() {
    this.selectedConnection.set(null);
    this.comparisonData.set(null);
  }

  // ================= API Functions =================

  /**
   * Fetches data for target dropped node based on type and custom date ranges
   * ยิงเรียก API เพื่อดึงข้อมูลตามประเภทของการ์ดและฟิลเตอร์ช่วงวันที่
   */
  fetchDataForNode(node: DashboardNode, siteId: string) {
    const startStr = this.formatDate(node.startDate);
    const endStr = this.formatDate(node.endDate);

    if (node.type === 'total-cars') {
      this.apiService.getTotalCarsData(siteId, startStr, endStr).subscribe({
        next: (dto) => {
          node.data = this.mapperService.mapTotalCarsDtoToModel(dto);
          node.isLoading = false;
          this.updateConnectionLines();
        },
        error: (err) => {
          console.error('[API Error] Fetching total cars failed:', err);
          node.isLoading = false;
        }
      });
    } else if (node.type === 'revenue') {
      this.apiService.getRevenueSummaryData(siteId, startStr, endStr).subscribe({
        next: (dto) => {
          node.data = this.mapperService.mapRevenueSummaryDtoToModel(dto);
          node.isLoading = false;
          this.updateConnectionLines();
        },
        error: (err) => {
          console.error('[API Error] Fetching revenue failed:', err);
          node.isLoading = false;
        }
      });
    } else if (node.type === 'occupancy') {
      this.apiService.getOccupancyData(siteId, startStr, endStr).subscribe({
        next: (dto) => {
          node.data = this.mapperService.mapOccupancyDtoToModel(dto);
          node.isLoading = false;
          this.updateConnectionLines();
        },
        error: (err) => {
          console.error('[API Error] Fetching occupancy failed:', err);
          node.isLoading = false;
        }
      });
    } else if (node.type === 'transactions') {
      this.apiService.getRecentTransactionsData(siteId, startStr, endStr).subscribe({
        next: (dtoList) => {
          node.data = dtoList.map(dto => this.mapperService.mapRecentTransactionDtoToModel(dto));
          node.isLoading = false;
          this.updateConnectionLines();
        },
        error: (err) => {
          console.error('[API Error] Fetching recent transactions failed:', err);
          node.isLoading = false;
        }
      });
    } else if (node.type === 'monthly-stats') {
      this.apiService.getMonthlyStatsData(siteId, startStr, endStr).subscribe({
        next: (dto) => {
          node.data = this.mapperService.mapMonthlyStatsDtoToModel(dto);
          node.isLoading = false;
          this.updateConnectionLines();
        },
        error: (err) => {
          console.error('[API Error] Fetching monthly stats failed:', err);
          node.isLoading = false;
        }
      });
    }
  }
}
